import I18n from 'core/i18n'
import * as moment from 'moment'
import * as React from 'react'
import { SingleDatePicker } from 'react-dates'
import Select from 'react-select'
import styled, { AnyStyledComponent, createGlobalStyle } from 'styled-components'
import { postService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Form } from '../../../molecules'
import { IWeeklyPost } from 'utils/weeklyTypes'

declare var window: interfaces.IWindow

const FIELDS = {
  day: 'day',
  postId: 'post_id',
  postStartTime: 'post_start_time',
}
export const TIME_OPTIONS = [
  { id: 'am', label: 'AM' },
  { id: 'pm', label: 'PM' },
  { id: 'all_day', label: 'All Day' },
]

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
  menu: styles => ({
    ...styles,
    fontSize: 12,
  }),
  menuList: styles => ({
    ...styles,
    maxHeight: 400,
  }),
  multiValue: styles => ({
    ...styles,
    backgroundColor: constants.COLORS.primary,
  }),
  multiValueLabel: styles => ({
    ...styles,
    color: constants.COLORS.white,
  }),
  multiValueRemove: styles => ({
    ...styles,
    color: constants.COLORS.white,
  }),
}

interface IProps {
  posts: Array<interfaces.IPost | IWeeklyPost>
  conversationId: number
  user: interfaces.IUser
  selectedTour: any
  selectedDate: moment.Moment
  selectedStartTime: { value: string; label: string }
  setSelectedTour: React.Dispatch<any>
  setSelectedDate: React.Dispatch<React.SetStateAction<moment.Moment>>
  setSelectedStartTime: React.Dispatch<React.SetStateAction<{ value: string; label: string }>>
  getMessages(): Promise<void>
}

type CheckTimeZone =
  | {
      am: boolean
      pm: boolean
      all_day: boolean
    }
  | undefined

const PreAssignForm: React.FC<IProps> = ({
  posts,
  selectedDate,
  selectedTour,
  selectedStartTime,
  setSelectedTour,
  setSelectedStartTime,
  setSelectedDate,
}) => {
  const [startTimes, setStartTimes] = React.useState([])
  const [dateFocused, setStartDateFocused] = React.useState(null)
  const handleCalendarOutSideRange = React.useCallback((calendarMoment: moment.Moment) => {
    return calendarMoment.isBefore(moment())
  }, [])

  const onStartDateChange = React.useCallback((date: moment.Moment | null) => {
    setSelectedDate(date)
  }, [])

  const currentPost = React.useMemo(() => {
    return posts.find(({ id }) => id === selectedTour?.value)
  }, [selectedTour])

  React.useEffect(() => {
    if (selectedTour && selectedDate) {
      postService.getStartTimes(selectedTour.value, selectedDate).then(start_times => {
        setStartTimes(start_times.length > 0 ? start_times : [])
        if (
          start_times.length === 0 ||
          !start_times.find(start_time => selectedStartTime?.value === start_time.start_time)
        ) {
          setSelectedStartTime({ value: '', label: '' })
        }
      })
    } else {
      setStartTimes([])
      setSelectedStartTime({ value: '', label: ' ' })
    }
  }, [selectedTour, selectedDate])

  const filterOption = React.useCallback((option, inputValue) => {
    const keywords = inputValue.split(/[\s　]+/).filter(keyword => keyword.trim() !== '')

    if (keywords.length === 0) {
      return true
    }

    const targetText = `${option.label} ${option.value}`.toLowerCase()
    return keywords.every(keyword => targetText.includes(keyword.toLowerCase()))
  }, [])

  const postOptions = React.useMemo(() => {
    return [
      { value: '', label: '-' },
      ...posts.map(post => ({
        value: post.id,
        label: `${utilService.getId4digit(post.id)} | ${post.short_name || post.name}`,
      })),
    ]
  }, [posts])

  return (
    <S.Wrapper>
      <Form fields={FIELDS}>
        <S.DateRangePickerCustomStyle />
        <SingleDatePicker
          id="day"
          date={selectedDate}
          placeholder="Date"
          onDateChange={onStartDateChange}
          focused={dateFocused}
          onFocusChange={changed => setStartDateFocused(changed.focused)}
          enableOutsideDays={false}
          isOutsideRange={handleCalendarOutSideRange}
          numberOfMonths={2}
          hideKeyboardShortcutsPanel={true}
        />

        <S.FormItem>
          <Select
            name="postId"
            placeholder={
              selectedTour ? (
                'Select Tour'
              ) : (
                <span className="text-danger">{I18n.t('generic.tour_required')}</span>
              )
            }
            options={postOptions}
            isMulti={false}
            onChange={(option: any) => {
              if (option.value === '') {
                setSelectedTour(null)
              } else {
                setSelectedTour(option)
              }
            }}
            styles={selectCustomStyles}
            value={selectedTour}
            defaultValue={selectedTour}
            filterOption={filterOption}
          />
        </S.FormItem>
        <S.FormItem>
          <Select
            name="postStartTime"
            placeholder={
              currentPost ? (
                'Select Start Time'
              ) : (
                <span className="text-danger">Select Start Time</span>
              )
            }
            options={startTimes.map(start_time => ({
              value: start_time.start_time,
              label: start_time.start_time,
            }))}
            isMulti={false}
            onChange={setSelectedStartTime}
            styles={selectCustomStyles}
            value={selectedStartTime}
          />
        </S.FormItem>
      </Form>
    </S.Wrapper>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}
S.Wrapper = styled.div`
  margin-bottom: 12px;
  padding: 0 1em;
`
S.FormItem = styled.div`
  margin-top: 16px;

  .text-danger {
    color: ${constants.COLORS.cautionText};
  }
`
S.DateRangePickerCustomStyle = createGlobalStyle`
  .SingleDatePicker {
    display: block;
    margin-top: 24px;
  }

  .DateRangePicker.DateRangePicker_1 {
    width: 100%;
  }

  .DateRangePickerInput.DateRangePickerInput_1 {
    width: 100%;
    border: none;
  }

  .DateRangePickerInput_arrow.DateRangePickerInput_arrow_1 {
    display: none;
  }

  .SingleDatePickerInput__withBorder {
    width: 100%;
    border-color: ${constants.COLORS.divider};
    border-radius: 4px;
  }
  .DateInput_input {
    font-size: 15px;
    padding: 8px 12px;
  }
  .DateInput {
    width: 100%;
  }

  .CalendarDay__selected_span {
    background: ${constants.COLORS.secondary};
    border: 1px double ${constants.COLORS.secondary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__selected {
    background: ${constants.COLORS.primary};
    border: 1px double ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__selected:hover {
    background: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
  }

  .CalendarDay__hovered_span:hover,
  .CalendarDay__hovered_span {
    background: ${constants.COLORS.secondary};
    border: 1px double ${constants.COLORS.secondary};
    color: ${constants.COLORS.white};
  }
`
S.Radios = styled.div`
  display: flex;
`

S.Radio = styled.div`
  display: flex;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }

  .Radio_Main {
    flex: 1;

    > label {
      position: relative;
      cursor: pointer;
      &.disabled {
        pointer-events: none;
        opacity: 0.5;
      }

      &:hover {
        opacity: 0.75;
      }

      input {
        opacity: 0;
        height: 0;
        width: 0;
      }

      .Radio_Icon {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        margin: auto;
        width: 24px;
        height: 24px;

        i {
          position: absolute;
          top: 0;
          left: 0;

          &.filled {
            display: none;
            color: ${constants.COLORS.primary};
          }

          &.outline {
            display: block;
            color: ${constants.COLORS.divider};
          }
        }
      }
      input:checked + .Radio_Icon {
        i {
          &.filled {
            display: block;
          }

          &.outline {
            display: none;
          }
        }
      }

      span {
        font-size: 14px;
        padding-left: 28px;
      }
    }

    > label + label {
      margin-left: 16px;
    }
  }
`

export default PreAssignForm
